import {
  DeleteOutlined,
  EyeOutlined,
  PlusOutlined
} from '@ant-design/icons';
import {
  Button,
  message,
  Modal,
  Space, Tooltip, Typography
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { AxiosError } from 'axios';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAppSelector } from 'app/store';
import HeaderPage from 'common/components/HeaderPage';
import PageTable from 'common/components/PageTable';
import {
  FilterDataProps,
  FilterValueProps,
  mappingFilterFields,
  mappingFilterToQuery,
  mappingParamsFilter,
  mappingQueryParamsFilter
} from 'common/components/PageTable/filter';
import { deleteStaffFulltimeService, getAllStaffFulltimeService } from 'common/services/extends/staffFulltime';
import { DepartmentEnum } from 'common/services/extends/staffFulltime/types';
import { ROUTE_PATHS } from 'common/utils/constant';
import { detectError, formatDateDDMMYYYY } from 'common/utils/functions';

export type TableDataTypes = {
  id: number,
  fullname: string,
  idCardNumber: string,
  department?: Array<DepartmentEnum>,
  dob?: string,
  idCardIssuedAt?: string,
  idCardIssuedBy?: string,
  permanentAddress?: string,
  team?: string,
  phoneNumber?: string,
  createdAt: string,
  updatedAt: string,
  user?: {
    id: number,
    name: string,
  }
};

const EmployeeFullTimeManagement: React.FC<ActiveRoles> = ({
  roleUpdate, roleDelete, roleCreate, roleIndex
}) => {
  /* Hooks */
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  /* Selectors */
  const {
    defaultPageSize, advancedFilter
  } = useAppSelector((state) => state.system);

  /* Search Params */
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => {
    const paramsObj = { ...Object.fromEntries(searchParams.entries()) };
    if (searchParams.has('page')) delete paramsObj.page;
    return paramsObj;
  }, [searchParams]);
  const pageParam = searchParams.get('page');

  /* States */
  const [currentPage, setCurrentPage] = useState(Number(pageParam));
  const [currentView, setCurrentView] = useState(defaultPageSize);
  const [keyword, setKeyword] = useState('');
  const [selectedFilterList, setSelectedFilterList] = useState<
    FilterValueProps[]>(mappingQueryParamsFilter(params));

  const returnParamsFilter = useMemo(
    () => mappingParamsFilter(selectedFilterList),
    [selectedFilterList]
  );

  const isFirstRender = React.useRef(true);

  const queryKeys = ['get-employee-fullname-list', currentPage, currentView, selectedFilterList, keyword];

  /* Queries */
  const { data: employeeRes, isLoading } = useQuery(
    queryKeys,
    () => getAllStaffFulltimeService({
      page: currentPage,
      limit: currentView,
      ...(keyword && { keyword }),
      ...returnParamsFilter
    }),
    {
      enabled: currentPage > 0,
      onError: (error: unknown) => {
        if (error instanceof AxiosError) {
          const status = error.response?.status;
          message.error(t(detectError(status)));
        }
      }
    }
  );

  const { mutate: deleteMutate, isLoading: deleteLoading } = useMutation(
    ['delete-employees-fulltime'],
    deleteStaffFulltimeService,
    {
      onSuccess: () => {
        // Invalidate the query to refetch the data
        queryClient.invalidateQueries(queryKeys);
        message.success(t('message.deleteSuccess'));
      },
      onError: () => {
        message.error(t('message.deleteError'));
      }
    }
  );

  const handleDelete = (ids: number[]) => {
    deleteMutate(ids);
  };
  /* Datas */

  const tableData = useMemo(() => employeeRes?.data.map((item) => ({
    user: item.user,
    ...item.staffFullTimeData,
  })) || [], [employeeRes]);

  /* Variables */
  const columns: ColumnsType<TableDataTypes> = [
    {
      title: t('staffFulltime.fullname'),
      dataIndex: 'name',
      key: 'name',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.fullname}
        </Typography.Text>
      ),
    },
    {
      title: t('system.phone'),
      dataIndex: 'phone',
      key: 'phone',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.phoneNumber}
        </Typography.Text>
      ),
    },
    {
      title: t('staffFulltime.dob'),
      dataIndex: 'dob',
      key: 'dob',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.dob, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('staffFulltime.team'),
      dataIndex: 'team',
      key: 'team',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.team}
        </Typography.Text>
      ),
    },
    {
      title: t('staffFulltime.department'),
      dataIndex: 'department',
      key: 'department',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.department?.join(', ')}
        </Typography.Text>
      ),
    },
    {
      title: t('staffFulltime.accountSystem'),
      dataIndex: 'account',
      key: 'account',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.user?.name || ''}
        </Typography.Text>
      ),
    },
    {
      title: t('staffFulltime.identityNumber'),
      dataIndex: 'identityNumber',
      key: 'identityNumber',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.idCardNumber}
        </Typography.Text>
      ),
    },
    {
      title: t('staffFulltime.grantDate'),
      dataIndex: 'grantDate',
      key: 'grantDate',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {formatDateDDMMYYYY(data.idCardIssuedAt, '/')}
        </Typography.Text>
      ),
    },
    {
      title: t('staffFulltime.grantPlace'),
      dataIndex: 'grantPlace',
      key: 'grantPlace',
      render: (_: string, data: TableDataTypes) => (
        <Typography.Text>
          {data.idCardIssuedBy}
        </Typography.Text>
      ),
    },
    {
      title: t('system.action'),
      key: 'action',
      width: 100,
      align: 'center',
      fixed: 'right',
      render: (_: string, _data: TableDataTypes) => (
        <Space>
          <Tooltip title={t('system.viewDetail')}>
            <Button
              disabled={!roleIndex}
              icon={<EyeOutlined />}
              onClick={() => navigate(`${ROUTE_PATHS.EMPLOYEE_FULLTIME_DETAIL}?id=${_data.id}`)}
            />
          </Tooltip>
          <Tooltip title={t('system.delete')}>
            <Button
              disabled={!roleDelete}
              icon={<DeleteOutlined />}
              onClick={() => {
                Modal.confirm({
                  className: 't-pagetable_deleteRecordModal',
                  autoFocusButton: 'cancel',
                  okText: t('system.ok'),
                  cancelText: t('system.cancel'),
                  cancelButtonProps: {
                    type: 'primary',
                  },
                  okButtonProps: {
                    type: 'default',
                  },
                  title: t('message.confirmDeleteRecord'),
                  onOk: () => {
                    handleDelete([_data.id]);
                  },
                });
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleSearch = (val: string) => {
    setKeyword(val);
  };

  const handleSetCurrentPage = (page: number) => {
    setCurrentPage(page);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList), page: page.toString()
    });
  };

  const handleSetCurrentView = (view: number) => {
    setCurrentView(view);
  };

  const handleFilter = (data: FilterValueProps) => {
    const typeFilter = String(data.filter).split('.')[1];
    if ((typeFilter === 'isNull' || typeFilter === 'isNotNull') && selectedFilterList.find((item) => item.key === data.key)) {
      return;
    }
    const counter = selectedFilterList.filter(
      (item) => item.field === data.field && item.filter === data.filter
    ).length;
    setSelectedFilterList([...selectedFilterList, { ...data, index: counter }]);
    handleSetCurrentPage(1);
  };

  const handleDeleteFilter = (key: string, index?: number) => {
    const tempList = selectedFilterList.slice();
    setSelectedFilterList(tempList.filter((item) => !(item.key === key && item.index === index)));
    handleSetCurrentPage(1);
  };

  const filterFields: FilterDataProps[] = useMemo(
    () => mappingFilterFields('staffFullTime', advancedFilter),
    [advancedFilter]
  );

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    setCurrentPage(1);
    setSearchParams({
      ...mappingFilterToQuery(selectedFilterList),
      page: '1'
    }, { replace: true });
  }, [keyword, setSearchParams, selectedFilterList]);

  useEffect(() => {
    if (pageParam) {
      setCurrentPage(Number(pageParam));
    } else {
      setCurrentPage(1);
    }
  }, [pageParam]);

  /* Render */
  return (
    <>
      <HeaderPage
        fixed
        title={t('sidebar.employeeFullTime')}
        rightHeader={(
          <Space>
            <Button
              type="primary"
              disabled={!roleCreate}
              onClick={() => navigate(ROUTE_PATHS.EMPLOYEE_FULLTIME_DETAIL)}
            >
              <PlusOutlined />
              {t('system.create')}
            </Button>
          </Space>
        )}
      />
      <div className="t-mainlayout_wrapper">
        <PageTable
          noCheckbox
          isLoading={isLoading || deleteLoading}
          roles={{
            roleCreate,
            roleDelete,
            roleUpdate
          }}
          handleSearch={handleSearch}
          filtersDataTable={{
            handleFilter,
            selectedFilterList,
            handleDeleteFilter,
          }}
          tableProps={{
            initShowColumns: ['name', 'phone', 'dob', 'team', 'department', 'account', 'identityNumber', 'grantDate', 'grantPlace', 'action'],
            columns,
            pageData: tableData,
            currentPage,
            pageSize: currentView,
            handleSetCurrentPage,
            handleSetCurrentView,
            total: employeeRes?.meta.total || 0,
            noBaseCol: true,
            noDeleteLanguage: true,
            filterFields
          }}
        />
      </div>
    </>
  );
};

export default EmployeeFullTimeManagement;
